<script>
  import Page from '@components/page.svelte'
  import Table from '@components/table'
  import FilterBar from '@components/table/filter-bar.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import BoxedText from '@components/general/boxed-text.svelte'
  import Button from '@components/buttons/button.svelte'
  import AdminForm from '@components/users/admin-form.svelte'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import { useSelection } from '@hooks/use-selection.svelte'
  import { page, router } from '@inertiajs/svelte'
  import { CirclePlus, EllipsisVertical, Pencil, Trash2 } from 'lucide-svelte'
  import { sortItems } from '@lib/utils'
  import { formatDate } from '@lib/utils/date'

  let { users = [], pagination, ids = [], is_superadmin = false } = $props()

  let currentUser = $derived($page.props.user)
  let selection = $derived(useSelection({ initialItems: users, allIds: ids }))
  let sort = usePageParam({ key: "sort", defaultValue: "newest", items: sortItems })

  let formOpen = $state(false)
  let selectedUser = $state(null)

  function selectUser(user) {
    selectedUser = user
    formOpen = true
  }

  function deleteUser(user) {
    if (!window.confirm(`Are you sure you want to delete ${user.name}? This cannot be undone.`)) return
    router.delete(`/admin/users/${user.id}`)
  }

  function isSelf(user) {
    return user.id === currentUser.id
  }
</script>

<Page title="Admin Users" subtitle="Manage admin users" links={[{ text: "Admin", href: "/admin" }]}>

  {#snippet buttons()}
    {#if is_superadmin}
      <Button text="Create admin user" onclick={() => formOpen = true} Icon={CirclePlus} />
    {/if}
  {/snippet}

  <FilterBar {pagination} paginationProps={{ summary: true, terms: ["user", "users"] }}>
    {#snippet filters()}

      <!-- Sort -->
      <Dropdown buttonProps={{ text: sort.label || "Sort", size: "md", Icon: sort.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each sort.items as sortItem (sortItem.value)}
            {@render item({ text: sortItem.label, icon: sortItem.icon, onSelect: () => sort.setValue(sortItem.value) })}
          {/each}
        {/snippet}
      </Dropdown>

    {/snippet}
  </FilterBar>

  <Table.Root empty={users.length === 0} emptyMessage="No admin users found">
    <Table.Head>
      <Table.SelectionDropdownCell {selection} />
      <Table.Header width="25%">Name</Table.Header>
      <Table.Header width="25%">Email</Table.Header>
      <Table.Header width="15%" align="center">Role</Table.Header>
      <Table.Header width="15%" align="center">Since</Table.Header>
      {#if is_superadmin}
        <Table.Header width="10%" align="center">Actions</Table.Header>
      {/if}
    </Table.Head>
    <Table.Body>
      {#each users as user (user.id)}
        {@const roleVariant = user.role === "superadmin" ? "info" : "default"}
        <Table.Row>
          <Table.Cell width="3rem" align="center">
            <input type="checkbox" checked={selection.has(user.id)} onclick={e => selection.toggle(user.id, e)} />
          </Table.Cell>
          <Table.Cell width="25%">
            {user.name}
            {#if isSelf(user)}
              <span class="text-xs text-gray-400 ml-1">(you)</span>
            {/if}
          </Table.Cell>
          <Table.Cell width="25%">{user.email_address}</Table.Cell>
          <Table.Cell width="15%" align="center">
            <BoxedText variant={roleVariant} size="xs" text={user.role} />
          </Table.Cell>
          <Table.Cell width="15%" align="center">{formatDate(user.created_at)}</Table.Cell>
          {#if is_superadmin}
            <Table.Cell width="10%" align="center">
              <Dropdown buttonProps={{ variant: "naked", Icon: EllipsisVertical }} showTriggerButton={false} width="16rem">
                {#snippet contents(item)}
                  {@render item({ text: "Edit role", icon: Pencil, onSelect: () => selectUser(user), disabled: isSelf(user) })}
                  {@render item({ text: "Delete", icon: Trash2, onSelect: () => deleteUser(user), disabled: isSelf(user) })}
                {/snippet}
              </Dropdown>
            </Table.Cell>
          {/if}
        </Table.Row>
      {/each}
    </Table.Body>
  </Table.Root>

  <Table.Pagination {pagination} paginationProps={{ summary: true, terms: ["user", "users"] }} />
  
</Page>

<AdminForm bind:open={formOpen} user={selectedUser} onClose={() => selectedUser = null} />
